import type { ReactElement } from 'react';
import type { BoardPageBoard } from '@molt/shared';
import { LayoutTemplate } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';
import type { DragHandle } from '@/lib/list-drag';
import { CardSurface } from '../CardSurface';
import { BoardStarButton } from './BoardStarButton';

/**
 * A board on the boards landing page — `FE-23.1`, chunk `D2`, agent E.
 *
 * **The product's second `--on-board` site** (`entry-surfaces.md` **D8**), and
 * `CardSurface variant="tile"` already owns every rule that makes it correct:
 * `data-board-text` is set from `background.textTreatment` **only when a
 * background was actually chosen**, and the title always carries
 * `--text-shadow-on-board` because no fixed ink pair clears AA on a mid-tone
 * board (4.14:1 worst case). **Pass `background` through; do not
 * re-implement any of it here.**
 *
 * ⚠ **A tile is a stretched link plus sibling controls.** P2-04 adds the star
 * button, so the previous `CardSurface as="a"` shape would become invalid
 * nested interactive markup. The anchor is now the empty stretched link with
 * the board title as its accessible name; the visible title and star are
 * siblings.
 *
 * ⚠ `boardSchema.title` is `.max()` with **no `.min(1)`** on read — the read
 * schema describes what the database can legitimately hold. Falls back to
 * *"Untitled board"* rather than rendering an empty link.
 *
 * Unlike `CardTile` (`components/board/CardTile.tsx`), this tile's only extra
 * control is the star, so the stretched link remains the title-named
 * navigation target while sibling controls stay outside it.
 */
export interface BoardTileProps {
  board: BoardPageBoard;
  starred?: boolean | undefined;
  starBusy?: boolean | undefined;
  onToggleStar?: ((board: BoardPageBoard, nextStarred: boolean) => void) | undefined;
  dragHandle?: DragHandle | undefined;
  className?: string;
}

export function BoardTile({
  board,
  starred = false,
  starBusy,
  onToggleStar,
  dragHandle,
  className,
}: BoardTileProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const title = board.title.trim() === '' ? t('boards.untitledBoard') : board.title;
  const href = `/b/${board.shortLink}/${board.slug}`;

  return (
    <CardSurface
      as="div"
      variant="tile"
      interactive
      background={board.background}
      className={cn(
        'relative flex h-24 flex-col justify-end p-3 text-on-board',
        className,
      )}
    >
      <a
        ref={dragHandle?.setActivatorNodeRef}
        href={href}
        aria-label={title}
        className="absolute inset-0 rounded-card focus-visible:-outline-offset-2"
        {...dragHandle?.attributes}
        {...dragHandle?.listeners}
      />
      {onToggleStar !== undefined && (
        <BoardStarButton
          boardTitle={title}
          starred={starred}
          busy={starBusy}
          className="absolute right-2 top-2 z-10 bg-overlay/40 text-board-ink-light hover:bg-surface-raised hover:text-ink"
          onToggle={(nextStarred) => onToggleStar(board, nextStarred)}
        />
      )}
      {board.isTemplate ? (
        <span className="pointer-events-none absolute left-2 top-2 z-10 inline-flex h-5 items-center gap-1 rounded-card border border-white/45 bg-overlay/55 px-1.5 text-xs font-semibold text-board-ink-light text-shadow-on-board">
          <LayoutTemplate aria-hidden="true" className="size-3" />
          {t('boards.templateBadge')}
        </span>
      ) : null}
      <span className="pointer-events-none relative pr-8 text-card font-medium text-shadow-on-board line-clamp-2">
        {title}
      </span>
    </CardSurface>
  );
}
